import type { PlanCode, PrototypeRuntime, Root } from './prototypeTypes';
import type { TopicPageModel } from '../../shared/pages/pageModels';

type TopicReturnValueKey = 'detail' | 'chapters' | 'keywords' | 'resume';

interface TopicReturnSnapshot {
	slug: string;
	viewerScope: string;
	plan: PlanCode;
	generation: number;
	detail?: string;
	chapters?: string;
	keywords?: string;
	resume?: string;
}

const snapshots = new WeakMap<Root, TopicReturnSnapshot>();

export function markTopicReturnSnapshot(root: Root, slug: string, runtime: PrototypeRuntime, plan: PlanCode): void {
	const viewerScope = topicViewerScope(runtime);
	const current = snapshots.get(root);
	if (current?.slug === slug && current.viewerScope === viewerScope) return;
	snapshots.set(root, { slug, viewerScope, plan, generation: 0 });
}

/** Seeds the comparison baseline from the already-visible SSR Topic model. */
export function primeSsrTopicReturnSnapshot(root: Root, model: TopicPageModel, runtime: PrototypeRuntime, plan: PlanCode): void {
	markTopicReturnSnapshot(root, model.topic.slug, runtime, plan);
	rememberTopicReturnValue(root, 'detail', {
		id: model.topic.id,
		slug: model.topic.slug,
		title: model.topic.title,
		subtitle: model.topic.subtitle,
		summary: model.topic.summary,
		category: model.topic.category,
		isPublic: model.topic.source === 'private_ai' ? 0 : 1,
		keyTakeaways: model.topic.keyTakeaways,
		faqs: model.topic.faqs,
		chapterCount: model.chapters.length,
		audioMinutes: model.chapters.reduce((sum, chapter) => sum + durationMinutes(chapter.duration), 0),
		engagement: {
			chapters: model.chapters.length,
			mediaMinutes: model.chapters.reduce((sum, chapter) => sum + durationMinutes(chapter.duration), 0),
			opened: model.engagement.opened,
			saved: model.engagement.saved,
		},
	});
	rememberTopicReturnValue(root, 'chapters', model.chapters);
	rememberTopicReturnValue(root, 'keywords', model.topic.keywords);
}

export function canReconcileTopicReturn(root: Root, slug: string, runtime: PrototypeRuntime): boolean {
	const current = snapshots.get(root);
	return current?.slug === slug && current.viewerScope === topicViewerScope(runtime);
}

export function beginTopicReturnReconciliation(root: Root, slug: string, runtime: PrototypeRuntime): number | null {
	const current = snapshots.get(root);
	if (!current || current.slug !== slug || current.viewerScope !== topicViewerScope(runtime)) return null;
	current.generation += 1;
	return current.generation;
}

/** Invalidates an in-flight return refresh before a local progress rollback repaints the CTA. */
export function invalidateTopicReturnReconciliation(root: Root, slug: string, runtime: PrototypeRuntime): void {
	const current = snapshots.get(root);
	if (!current || current.slug !== slug || current.viewerScope !== topicViewerScope(runtime)) return;
	current.generation += 1;
}

export function isCurrentTopicReturnReconciliation(root: Root, slug: string, runtime: PrototypeRuntime, generation: number): boolean {
	const current = snapshots.get(root);
	return Boolean(
		current && current.slug === slug && current.viewerScope === topicViewerScope(runtime) && current.generation === generation,
	);
}

export function topicReturnValueChanged(root: Root, key: TopicReturnValueKey, value: unknown): boolean {
	return snapshots.get(root)?.[key] !== fingerprint(key, value);
}

export function rememberTopicReturnValue(root: Root, key: TopicReturnValueKey, value: unknown): void {
	const current = snapshots.get(root);
	if (current) current[key] = fingerprint(key, value);
}

export function topicReturnPlanChanged(root: Root, plan: PlanCode): boolean {
	return snapshots.get(root)?.plan !== plan;
}

export function rememberTopicReturnPlan(root: Root, plan: PlanCode): void {
	const current = snapshots.get(root);
	if (current) current.plan = plan;
}

function topicViewerScope(runtime: PrototypeRuntime): string {
	if (!runtime.userId) return 'visitor';
	return `user:${runtime.userId}|session:${runtime.sessionHash || ''}`;
}

function fingerprint(key: TopicReturnValueKey, value: unknown): string {
	if (key === 'detail') return JSON.stringify(normalizeDetailEvidence(value));
	if (key === 'chapters') return JSON.stringify(normalizeChapterEvidence(value));
	if (key === 'keywords') return JSON.stringify(normalizeKeywordEvidence(value));
	return JSON.stringify(value);
}

function normalizeDetailEvidence(value: unknown): unknown {
	if (!value || typeof value !== 'object') return value;
	const detail = value as Record<string, unknown>;
	const engagement = detail.engagement && typeof detail.engagement === 'object'
		? detail.engagement as Record<string, unknown>
		: {};
	return {
		id: text(detail.id),
		slug: text(detail.slug),
		title: text(detail.title),
		subtitle: text(detail.subtitle),
		summary: text(detail.summary),
		category: text(detail.category),
		isPublic: number(detail.isPublic),
		keyTakeaways: Array.isArray(detail.keyTakeaways) ? detail.keyTakeaways : Array.isArray(detail.outcomes) ? detail.outcomes : [],
		faqs: Array.isArray(detail.faqs) ? detail.faqs : [],
		chapterCount: number(detail.chapterCount),
		audioMinutes: number(detail.audioMinutes),
		engagement: {
			chapters: number(engagement.chapters),
			mediaMinutes: number(engagement.mediaMinutes),
			opened: number(engagement.opened),
			saved: number(engagement.saved),
		},
	};
}

function normalizeChapterEvidence(value: unknown): unknown {
	if (!Array.isArray(value)) return value;
	return value.map((chapter) => {
		const item = chapter && typeof chapter === 'object' ? chapter as Record<string, unknown> : {};
		return {
			id: text(item.id),
			index: number(item.index),
			title: text(item.title),
			subtitle: text(item.subtitle),
			duration: text(item.duration),
			hasAudio: Boolean(item.hasAudio),
			hasVideo: Boolean(item.hasVideo),
			summary: text(item.summary),
		};
	});
}

function normalizeKeywordEvidence(value: unknown): unknown {
	if (!Array.isArray(value)) return value;
	return value.map((keyword) => {
		const item = keyword && typeof keyword === 'object' ? keyword as Record<string, unknown> : {};
		return { keyword: text(item.keyword), coefficient: number(item.coefficient) };
	});
}

function text(value: unknown): string {
	return typeof value === 'string' ? value : '';
}

function number(value: unknown): number {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
}

function durationMinutes(value: unknown): number {
	const match = typeof value === 'string' ? value.match(/\d+/) : null;
	return match ? Number.parseInt(match[0], 10) : 0;
}
